<script setup lang="ts">
  import { computed } from 'vue';
  import { TrendingDown, TrendingUp } from 'lucide-vue-next';
  import { formatVariationPct } from '../model/format';

  interface Props {
    variationPct: number | null;
    comparisonLabel: string;
    text?: string;
    invert?: boolean;
    neutralWhenNull?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    text: undefined,
    invert: false,
    neutralWhenNull: false,
  });

  const isNeutral = computed(() => props.variationPct === null && props.neutralWhenNull);

  const isVisible = computed(() => props.variationPct !== null || isNeutral.value);

  const isImprovement = computed(() => {
    const variation = props.variationPct ?? 0;
    return props.invert ? variation <= 0 : variation >= 0;
  });

  const badgeText = computed(() => {
    if (isNeutral.value) return 'Sem comparativo';
    if (props.text !== undefined) return props.text;
    return props.variationPct === null ? '' : formatVariationPct(props.variationPct);
  });

  const pillClass = computed(() => {
    if (isNeutral.value) return 'bg-gray-02';
    return isImprovement.value ? 'bg-green-20' : 'bg-pink-13';
  });

  const icon = computed(() => (isImprovement.value ? TrendingUp : TrendingDown));

  const iconTestId = computed(() => (isImprovement.value ? 'trending-up' : 'trending-down'));

  const badgeKey = computed(
    () => `${pillClass.value}|${badgeText.value}|${props.comparisonLabel}`
  );

  defineExpose({ badgeKey });
</script>

<template>
  <div
    v-if="isVisible"
    :key="badgeKey"
    :data-testid="isNeutral ? 'variation-badge-neutral' : 'variation-badge'"
    class="badge-fade-in flex items-center gap-2"
  >
    <span
      data-testid="variation-badge-pill"
      class="flex min-h-7 shrink-0 items-center gap-1 rounded-full px-3 py-0.5 text-gray-07"
      :class="pillClass"
    >
      <component
        :is="icon"
        v-if="!isNeutral"
        :data-testid="iconTestId"
        :size="24"
        :stroke-width="1.5"
      />
      <span class="text-xs/4.5 font-medium">{{ badgeText }}</span>
    </span>
    <span class="min-w-0 flex-1 text-sm text-gray-03">{{ props.comparisonLabel }}</span>
  </div>
</template>

<style scoped>
  @media (prefers-reduced-motion: no-preference) {
    .badge-fade-in {
      animation: badge-fade-in 300ms ease-out;
    }

    @keyframes badge-fade-in {
      from {
        opacity: 0;
      }
    }
  }
</style>
